/**
 * mediaService — mediasoup-client wrapper for Phase 1 video.
 *
 * Sits on top of socketService's rtc:* emit/ack wrappers. Owns:
 *   - a mediasoup-client Device (loaded from the router's rtpCapabilities)
 *   - one send transport (for publishing our webcam)
 *   - one recv transport (for consuming others' webcams)
 *   - publish/consume + per-publisher / per-viewer quality controls
 *
 * Anonymity: the ICE config (TURN servers + iceTransportPolicy) comes from the
 * server per RTC_TRANSPORT_MODE. In 'relay' mode the policy is 'relay', so the
 * browser only gathers TURN-relay candidates and never exposes the user's IP.
 * In 'direct' mode the policy is 'all' (lower latency, SFU sees client IP).
 */

import { Device } from 'mediasoup-client';
import socketService from './socketService';

class MediaService {
  constructor() {
    this.device = null;
    this.sendTransport = null;
    this.recvTransport = null;
    this.iceServers = [];
    this.iceTransportPolicy = 'relay';
    this.localStream = null;     // our published MediaStream
    this.producer = null;        // our video producer
    this.audioProducer = null;   // our audio producer (mic), published alongside
    this.consumers = new Map();  // producerId -> { consumer, stream }
    this.loaded = false;
    // In-flight promises so concurrent callers share one transport creation
    // (e.g. watching two users at once must not create two recv transports).
    this._sendTransportP = null;
    this._recvTransportP = null;
    // Optional callback fired when publishing stops on its OWN (e.g. the camera
    // track ends — device unplugged, permission revoked, another app grabs it).
    // App uses it to sync React state (drop the self tile, clear eyes). Not
    // called for an explicit unpublish() that App already initiated.
    this.onUnpublished = null;
    // True only between releaseVideoTrackForSwap() and the replaceTrack that
    // follows, so the producer's 'trackended' handler ignores the track we
    // stopped on purpose (an iOS camera swap) instead of tearing down the feed.
    this._swapping = false;
  }

  /**
   * Load the mediasoup Device from the router's RTP capabilities and capture
   * the ICE config. Idempotent. Returns the list of producers already present
   * in the room so the caller can mark those users as having video.
   */
  async load() {
    const res = await socketService.getRtpCapabilities();
    this.iceServers = res.iceServers || [];
    this.iceTransportPolicy = res.iceTransportPolicy || 'relay';

    if (!this.loaded) {
      this.device = new Device();
      await this.device.load({ routerRtpCapabilities: res.rtpCapabilities });
      this.loaded = true;
    }
    return res.producers || [];
  }

  /** Lazily create the send transport (for publishing). Concurrency-safe. */
  ensureSendTransport() {
    if (this.sendTransport) return Promise.resolve(this.sendTransport);
    if (this._sendTransportP) return this._sendTransportP;

    this._sendTransportP = (async () => {
      const { params } = await socketService.createTransport('send');
      const transport = this.device.createSendTransport({
        ...params,
        iceServers: this.iceServers,
        iceTransportPolicy: this.iceTransportPolicy,
      });

      transport.on('connect', ({ dtlsParameters }, callback, errback) => {
        socketService.connectTransport(transport.id, dtlsParameters).then(callback).catch(errback);
      });

      transport.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
        socketService
          .produce(transport.id, kind, rtpParameters, appData.outputQuality)
          .then(({ id }) => callback({ id }))
          .catch(errback);
      });

      this.sendTransport = transport;
      return transport;
    })();
    // Clear the in-flight holder on failure so a later attempt can retry.
    this._sendTransportP.catch(() => { this._sendTransportP = null; });
    return this._sendTransportP;
  }

  /** Lazily create the recv transport (for consuming). Concurrency-safe. */
  ensureRecvTransport() {
    if (this.recvTransport) return Promise.resolve(this.recvTransport);
    if (this._recvTransportP) return this._recvTransportP;

    this._recvTransportP = (async () => {
      const { params } = await socketService.createTransport('recv');
      const transport = this.device.createRecvTransport({
        ...params,
        iceServers: this.iceServers,
        iceTransportPolicy: this.iceTransportPolicy,
      });

      transport.on('connect', ({ dtlsParameters }, callback, errback) => {
        socketService.connectTransport(transport.id, dtlsParameters).then(callback).catch(errback);
      });

      this.recvTransport = transport;
      return transport;
    })();
    this._recvTransportP.catch(() => { this._recvTransportP = null; });
    return this._recvTransportP;
  }

  /**
   * Does the loaded Device's send capabilities include VP9? VP9 decode is
   * near-universal in modern browsers, but VP9 *encode* (what a publisher needs)
   * is the gate that an older client fails — in which case mediasoup-client
   * simply won't list VP9 in rtpCapabilities and we fall back to simulcast
   * (H.264 then VP8, per the server's codec preference order).
   */
  canPublishVp9() {
    const codecs = this.device?.rtpCapabilities?.codecs || [];
    return codecs.some((c) => /video\/vp9/i.test(c.mimeType));
  }

  /**
   * Publish the local webcam at the given output quality.
   * @param {MediaStream} stream - from getUserMedia({ video: true })
   * @param {'low'|'default'|'high'} outputQuality
   * @returns {string} producerId
   */
  async publish(stream, outputQuality = 'default') {
    await this.load();
    await this.ensureSendTransport();

    const track = stream.getVideoTracks()[0];
    if (!track) throw new Error('No video track to publish');

    this.localStream = stream;

    // Codec is chosen by capability gate (decided here, fixed for the producer's
    // life). Both paths deliver THREE spatial layers indexed 0=low/1=default/
    // 2=high so the server's maxSpatialLayer clamp is codec-agnostic; only the
    // SHAPE of `encodings` differs.
    //   - VP9 present  -> K-SVC: ONE encoding with scalabilityMode 'L3T3_KEY'.
    //     A single encode emits all 3 spatial + 3 temporal layers (less CPU,
    //     ~30-50% better quality/bitrate than VP8). L3T3_KEY (not L3T3) so upper
    //     spatial layers are independently decodable at key frames, letting the
    //     SFU drop/add a spatial layer cleanly — exactly like simulcast.
    //   - VP9 absent   -> 3-tier simulcast (the original path): three separate
    //     encodes at 1/4, 1/2, full resolution. The browser negotiates H.264 or
    //     VP8 (server preference order).
    // mediasoup-client negotiates the RTP either way and the server passes our
    // rtpParameters through unchanged. appData.outputQuality flows to the
    // 'produce' handler -> server, stored as the producer's maxSpatialLayer.
    const useVp9 = this.canPublishVp9();
    const encodings = useVp9
      ? [
          // 6 Mbps caps the full-res top spatial layer; the SVC layer split
          // below it is handled by the encoder per L3T3_KEY.
          { scalabilityMode: 'L3T3_KEY', maxBitrate: 6000000 },
        ]
      : [
          { scaleResolutionDownBy: 4, maxBitrate: 150000 },  // low (layer 0)
          { scaleResolutionDownBy: 2, maxBitrate: 1000000 }, // default (layer 1)
          { scaleResolutionDownBy: 1, maxBitrate: 6000000 }, // high (layer 2) — full-res 1080p60
        ];

    this.producer = await this.sendTransport.produce({
      track,
      encodings,
      // 6 Mbps is the "max" ceiling: full-res 1080p60 looks great here and
      // climbing higher spends bits the eye can't see. Start bitrate lifted so
      // the encoder ramps to a sharp picture immediately instead of crawling up.
      codecOptions: { videoGoogleStartBitrate: 2000 },
      appData: { outputQuality },
    });

    // Publish the mic alongside the cam as a SEPARATE audio producer (no
    // simulcast — audio has no spatial layers). Watchers consume it together
    // with the video into one tile stream; the sidebar mic toggle pauses/
    // resumes this producer. If the stream has no audio track (mic denied), we
    // simply publish video-only.
    const audioTrack = stream.getAudioTracks()[0];
    if (audioTrack) {
      this.audioProducer = await this.sendTransport.produce({
        track: audioTrack,
        // Opus tuning. The codec is declared stereo/48k server-side, but without
        // these the browser falls back to ~32kbps mono with FEC/DTX off:
        //   opusStereo  - actually carry the 2 channels the codec advertises
        //   opusFec     - in-band forward error correction (packet-loss recovery)
        //   opusDtx     - discontinuous transmission (stop sending during silence)
        //   maxAverageBitrate - lift the ceiling well above the mono default
        codecOptions: {
          opusStereo: true,
          opusFec: true,
          opusDtx: true,
          opusMaxAverageBitrate: 96000,
        },
        appData: { kind: 'audio' },
      });
    }

    // If the video track ends on its own (device unplugged, permission revoked,
    // grabbed by another app), tear down AND notify App so it can sync React
    // state — unpublish() alone changes no React state, leaving a dead tile.
    this.producer.on('trackended', () => {
      // Ignore the track ending if WE are mid-swap (releaseVideoTrackForSwap
      // stops the old track on purpose). Otherwise this is a real loss (device
      // unplugged, permission revoked, grabbed by another app): tear down AND
      // notify App so it can sync React state (unpublish() changes no React
      // state, which would leave a dead tile).
      if (this._swapping) return;
      this.unpublish();
      if (this.onUnpublished) this.onUnpublished();
    });
    return this.producer.id;
  }

  /** Change our publisher's output quality (re-caps all viewers server-side). */
  async setOutputQuality(quality) {
    if (!this.producer) return;
    await socketService.setOutputQuality(this.producer.id, quality);
  }

  /** Our own producer id (for self-monitor consume), or null if not publishing. */
  getOwnProducerId() {
    return this.producer ? this.producer.id : null;
  }

  /** Our own audio producer id, or null if not publishing audio. */
  getOwnAudioProducerId() {
    return this.audioProducer ? this.audioProducer.id : null;
  }

  // --- Track toggles: control WHAT is in our publish ---
  // Pause/resume a producer LOCALLY (stops sending RTP) and tell the SFU to stop
  // relaying it to watchers. Watchers keep their consumer open — the track just
  // goes black/silent — so toggling is reversible without re-handshaking.
  async pauseVideo() {
    if (!this.producer) return;
    this.producer.pause();
    await socketService.pauseProducer(this.producer.id);
  }

  async resumeVideo() {
    if (!this.producer) return;
    this.producer.resume();
    await socketService.resumeProducer(this.producer.id);
  }

  async pauseAudio() {
    if (!this.audioProducer) return;
    this.audioProducer.pause();
    await socketService.pauseProducer(this.audioProducer.id);
  }

  async resumeAudio() {
    if (!this.audioProducer) return;
    this.audioProducer.resume();
    await socketService.resumeProducer(this.audioProducer.id);
  }

  /**
   * Consume OUR OWN producer through the SFU so we monitor our actual published
   * output (post-encode, the layer others receive) rather than the raw camera.
   * The mic producer is bundled into the same stream/tile too (when we have
   * one), so the self tile can play back our own audio the way others hear it —
   * the tile opens muted (no feedback loop) and the user can unmute to
   * self-listen. This is a full SFU round-trip, so the audio is delayed relative
   * to the raw mic; that's expected for a "what others receive" monitor.
   * Returns the MediaStream, or null if not publishing.
   */
  async consumeSelf(desiredQuality = 'default') {
    if (!this.producer) return null;
    return this.consume(
      this.producer.id,
      desiredQuality,
      this.audioProducer ? this.audioProducer.id : null,
    );
  }

  /**
   * Hot-swap the published camera (and optionally the mic) to a new device's
   * tracks WITHOUT closing the producers — so watchers keep their consumers open
   * and the feed never drops. mediasoup-client's replaceTrack swaps the encoder's
   * source in place; the producer id (and thus every consumer) is untouched, so
   * no rtc:producer-closed fires and viewers don't have to re-watch.
   *
   * The producer keeps the encodings/codec it was created with (correct: the new
   * camera publishes at the same simulcast/SVC tiers). Returns false if we aren't
   * currently publishing video, so the caller can fall back to a full publish().
   *
   * @param {MediaStream} stream - fresh getUserMedia stream for the new device(s)
   * @param {boolean} micOn - whether the mic should be live after the swap
   * @returns {Promise<boolean>} true if the hot-swap happened
   */
  /**
   * Release ONLY the currently-published camera track (keeping the producer,
   * the audio track, and localStream object intact) so a single-camera platform
   * (iOS/iPadOS Safari) can acquire the OTHER camera before we replaceTrack it
   * in. The producer stays open the whole time — watchers' consumers are never
   * closed — so the feed survives a front↔back swap. Call right before the new
   * getUserMedia, then replaceTracks() with the fresh stream. No-op if not
   * publishing video.
   */
  /** Disarm the swap guard if an in-flight swap aborts before replaceTracks. */
  clearSwapGuard() {
    this._swapping = false;
  }

  releaseVideoTrackForSwap() {
    if (!this.producer || !this.localStream) return;
    // Arm the guard BEFORE stopping, so the 'trackended' that Safari may fire
    // synchronously on stop() doesn't tear down the producer. replaceTracks
    // clears it once the new track is swapped in.
    this._swapping = true;
    this.localStream.getVideoTracks().forEach((t) => t.stop());
  }

  async replaceTracks(stream, micOn = true, keepAudio = false) {
    if (!this.producer) { this._swapping = false; return false; }

    const newVideoTrack = stream.getVideoTracks()[0];
    if (!newVideoTrack) { this._swapping = false; throw new Error('No video track to swap in'); }

    // Swap the video source in place. The old track is stopped below (after the
    // swap) so the camera light goes off only once the new one is live.
    await this.producer.replaceTrack({ track: newVideoTrack });
    // New track is live on the producer now — the swap window is closed.
    this._swapping = false;
    // A swapped-in track is enabled by default; mirror the producer's pause state
    // (if the user had video paused, the new track shouldn't suddenly send RTP).
    if (this.producer.paused) newVideoTrack.enabled = false;

    // keepAudio: a video-ONLY swap (e.g. iOS front↔back flip with the mic
    // unchanged). The new `stream` has no audio track; leave the existing audio
    // producer + its live mic track completely untouched, and carry that live
    // audio track into the new localStream so self-monitor keeps its audio.
    let carryAudioTrack = null;
    if (keepAudio) {
      carryAudioTrack = this.localStream?.getAudioTracks?.()[0] || null;
    } else {
      // Mic: three transitions to cover.
      const newAudioTrack = stream.getAudioTracks()[0] || null;
      if (this.audioProducer && newAudioTrack) {
        // had mic -> still have mic: swap the source in place.
        await this.audioProducer.replaceTrack({ track: newAudioTrack });
        if (micOn) await this.resumeAudio();
        else await this.pauseAudio();
      } else if (this.audioProducer && !newAudioTrack) {
        // had mic -> new device has none: close the now-orphaned audio producer.
        try { await socketService.closeProducer(this.audioProducer.id); } catch (e) { /* noop */ }
        try { this.audioProducer.close(); } catch (e) { /* noop */ }
        this.audioProducer = null;
      } else if (!this.audioProducer && newAudioTrack) {
        // had no mic -> new device has one: there's no producer to replaceTrack
        // onto, so create a fresh audio producer (same Opus tuning as publish()).
        this.audioProducer = await this.sendTransport.produce({
          track: newAudioTrack,
          codecOptions: {
            opusStereo: true,
            opusFec: true,
            opusDtx: true,
            opusMaxAverageBitrate: 96000,
          },
          appData: { kind: 'audio' },
        });
        if (!micOn) await this.pauseAudio();
      }
    }

    // Release the OLD devices now that the new tracks are live, then adopt the
    // new stream as our published stream (and self-monitor source). On a
    // keepAudio swap, splice the still-live audio track into the new stream so we
    // don't stop it as an "old" track below and self-monitor keeps sound.
    if (keepAudio && carryAudioTrack) {
      stream.addTrack(carryAudioTrack);
    }
    if (this.localStream) {
      const keep = new Set(stream.getTracks());
      this.localStream.getTracks().forEach((t) => { if (!keep.has(t)) t.stop(); });
    }
    this.localStream = stream;
    return true;
  }

  /** Stop publishing and release the camera. */
  async unpublish() {
    if (this.producer) {
      const producerId = this.producer.id;
      // Close our own self-WATCH consumer (if open — created when you watch
      // yourself in the grid) so it doesn't orphan in the consumers map when the
      // producer goes away (e.g. the trackended path that calls unpublish()
      // without going through handleDisableWebcam).
      this.stopConsuming(producerId);
      try { await socketService.closeProducer(producerId); } catch (e) { /* noop */ }
      try { this.producer.close(); } catch (e) { /* noop */ }
      this.producer = null;
    }
    if (this.audioProducer) {
      const audioProducerId = this.audioProducer.id;
      try { await socketService.closeProducer(audioProducerId); } catch (e) { /* noop */ }
      try { this.audioProducer.close(); } catch (e) { /* noop */ }
      this.audioProducer = null;
    }
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = null;
    }
  }

  /**
   * Consume one producer and return its mediasoup consumer (already resumed).
   * Internal helper shared by video + audio consume.
   * @param {string} producerId
   * @param {'low'|'default'|'high'} desiredQuality
   * @returns {Consumer}
   */
  async _consumeOne(producerId, desiredQuality = 'default') {
    const { params } = await socketService.consume(
      this.recvTransport.id,
      producerId,
      this.device.rtpCapabilities,
      desiredQuality
    );

    const consumer = await this.recvTransport.consume({
      id: params.id,
      producerId: params.producerId,
      kind: params.kind,
      rtpParameters: params.rtpParameters,
    });

    // Server creates the consumer paused; resume now that we're wired up.
    await socketService.resumeConsumer(consumer.id);
    return consumer;
  }

  /**
   * Consume a remote user's webcam and return a MediaStream for their tile.
   * The VIDEO producer is the primary; if an audioProducerId is given, its
   * track is consumed too and bundled into the SAME stream (one tile, one
   * <video> element plays video + audio). The audio consumer is tracked under
   * its own producerId AND linked to the video record so stopConsuming(video)
   * tears both down.
   * @param {string} producerId - the video producer
   * @param {'low'|'default'|'high'} desiredQuality
   * @param {string|null} audioProducerId - the same user's audio producer, if any
   * @returns {MediaStream}
   */
  async consume(producerId, desiredQuality = 'default', audioProducerId = null) {
    await this.load();
    await this.ensureRecvTransport();

    if (this.consumers.has(producerId)) {
      return this.consumers.get(producerId).stream;
    }

    const consumer = await this._consumeOne(producerId, desiredQuality);
    const stream = new MediaStream([consumer.track]);

    // Bundle the user's audio into the same stream/tile when present and not
    // already consumed. Failure to get audio must not break the video tile.
    let audioConsumer = null;
    if (audioProducerId && !this.consumers.has(audioProducerId)) {
      try {
        audioConsumer = await this._consumeOne(audioProducerId, desiredQuality);
        stream.addTrack(audioConsumer.track);
        this.consumers.set(audioProducerId, { consumer: audioConsumer, stream });
      } catch (e) {
        console.warn('Audio consume failed (video unaffected):', e.message);
        audioConsumer = null;
      }
    }

    // Link the audio producerId on the video record so stopConsuming(video)
    // closes the bundled audio consumer too.
    this.consumers.set(producerId, {
      consumer,
      stream,
      audioProducerId: audioConsumer ? audioProducerId : null,
    });

    // Black-feed recovery: if no video frames decode shortly after consume, the
    // producer's one automatic keyframe request (fired on resume) was lost —
    // classically when this watch raced a republishing publisher (post-deploy).
    // Audio plays fine without keyframes, so the tile would sit black with live
    // sound forever. Ask the SFU to re-request a keyframe until frames flow.
    this._armKeyframeWatchdog(consumer);

    return stream;
  }

  /**
   * Watch a freshly-consumed VIDEO consumer for decoded frames; while none have
   * arrived, periodically ask the server to re-request a keyframe from the
   * producer. Self-terminating: stops when frames flow, the consumer closes, or
   * attempts are exhausted. No-op for audio consumers.
   */
  _armKeyframeWatchdog(consumer) {
    if (!consumer || consumer.kind !== 'video') return;
    const MAX_ATTEMPTS = 4;
    const INTERVAL_MS = 1500;
    let attempts = 0;
    const check = async () => {
      if (consumer.closed) return;
      let framesDecoded = 0;
      try {
        const stats = await consumer.getStats();
        stats.forEach((s) => {
          if (s.type === 'inbound-rtp' && (s.kind === 'video' || s.mediaType === 'video')) {
            framesDecoded = s.framesDecoded || 0;
          }
        });
      } catch (e) {
        return; // consumer torn down mid-poll — nothing to recover
      }
      if (framesDecoded > 0) return; // picture is flowing — done
      attempts += 1;
      console.log(`🖼️ No frames decoded yet — requesting keyframe (attempt ${attempts})`);
      socketService.requestKeyFrame(consumer.id).catch(() => { /* best effort */ });
      if (attempts < MAX_ATTEMPTS) setTimeout(check, INTERVAL_MS);
    };
    setTimeout(check, INTERVAL_MS);
  }

  /** Change desired quality for a producer we're watching (clamped to its max). */
  async setViewQuality(producerId, quality) {
    const rec = this.consumers.get(producerId);
    if (!rec) return;
    await socketService.setViewQuality(rec.consumer.id, quality);
  }

  /**
   * Stop consuming a producer (user unwatched, tile closed, or publisher left).
   * @param {string} producerId
   * @param {boolean} notifyServer - when true (the user explicitly unwatched),
   *   tell the server to close the server-side consumer so it doesn't leak and
   *   so the publisher's "watched-by" eye turns off. Skip it when the producer
   *   already closed (mediasoup auto-closed the consumer; publisher is gone).
   */
  stopConsuming(producerId, notifyServer = false) {
    const rec = this.consumers.get(producerId);
    if (rec) {
      if (notifyServer) {
        socketService.closeConsumer(rec.consumer.id).catch(() => { /* best effort */ });
      }
      try { rec.consumer.close(); } catch (e) { /* noop */ }
      this.consumers.delete(producerId);
      // A video record may have a bundled audio consumer (same tile/stream) —
      // tear it down with the video so audio doesn't keep playing/leaking.
      if (rec.audioProducerId) {
        this.stopConsuming(rec.audioProducerId, notifyServer);
      }
    }
  }

  isPublishing() {
    return !!this.producer;
  }

  // --- Stats handles (read-only; the "stats for nerds" panel polls these) ---
  // Return the live mediasoup-client objects for a producer we're WATCHING so a
  // caller can poll consumer.getStats() and read consumer.rtpParameters. The
  // audio consumer (bundled into the same tile) is returned alongside for the
  // audio-level / codec rows. Returns null if we're not consuming this producer.
  getConsumerStatsHandles(producerId) {
    const rec = this.consumers.get(producerId);
    if (!rec) return null;
    const audioRec = rec.audioProducerId ? this.consumers.get(rec.audioProducerId) : null;
    return {
      videoConsumer: rec.consumer,
      audioConsumer: audioRec ? audioRec.consumer : null,
    };
  }

  // For the SELF tile: our own producers. getStats() here yields outbound-rtp
  // (frames ENCODED, bytes SENT, target bitrate) rather than inbound. Null if
  // we aren't publishing.
  getProducerStatsHandles() {
    if (!this.producer) return null;
    return {
      videoProducer: this.producer,
      audioProducer: this.audioProducer || null,
    };
  }

  /** Tear everything down (logout / unmount). */
  reset() {
    this.unpublish();
    for (const producerId of Array.from(this.consumers.keys())) {
      this.stopConsuming(producerId);
    }
    try { this.sendTransport && this.sendTransport.close(); } catch (e) {}
    try { this.recvTransport && this.recvTransport.close(); } catch (e) {}
    this.sendTransport = null;
    this.recvTransport = null;
    this._sendTransportP = null;
    this._recvTransportP = null;
    this.device = null;
    this.loaded = false;
  }
}

const mediaServiceInstance = new MediaService();
// Dev-only debug handle so the publish/consume path can be exercised from the
// console / automated preview. No-op in production builds.
if (process.env.NODE_ENV !== 'production') {
  window.__mediaService = mediaServiceInstance;
}
export default mediaServiceInstance;
